import { useEffect, useState } from "react";
import { clientApi } from "@/lib/api";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatCurrency, formatDate } from "@/lib/format";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Layers, AlertTriangle, Building2, Briefcase } from "lucide-react";

const fmtPct = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${v}%`);
const CAT = { CAT_I: "Category I", CAT_II: "Category II", CAT_III: "Category III" };
const CALL_TONE = { PAID: "bg-emerald-50 text-emerald-700", PENDING: "bg-amber-100 text-amber-800", OVERDUE: "bg-red-100 text-red-700" };

function NetworthStrip() {
  const [nw, setNw] = useState(null);
  useEffect(() => { clientApi.get("/wealth/networth").then((r) => setNw(r.data)); }, []);
  if (!nw) return null;
  const parts = [["Mutual Funds", nw.mutual_funds], ["External MF", nw.external_mf], ["AIF", nw.aif], ["PMS", nw.pms]];
  return (
    <Card className="glass-card mb-6 border-0"><CardContent className="flex flex-wrap items-center gap-6 p-5">
      <div>
        <div className="text-xs text-[var(--ink-muted)]">Total Wealth with SMIFS</div>
        <div className="font-heading text-2xl text-[var(--brand)]" data-testid="networth-total">{formatCurrency(nw.total)}</div>
      </div>
      <div className="hidden h-10 w-px bg-[var(--glass-border)] sm:block" />
      {parts.map(([l, v]) => (
        <div key={l}>
          <div className="text-xs text-[var(--ink-muted)]">{l}</div>
          <div className="font-heading text-base text-[var(--brand)]">{formatCurrency(v)}</div>
        </div>
      ))}
      {nw.aif_pending_calls > 0 && (
        <div className="flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1.5 text-xs font-medium text-amber-800" data-testid="pending-call-alert">
          <AlertTriangle className="h-3.5 w-3.5" /> Capital calls pending: {formatCurrency(nw.aif_pending_calls)}
        </div>
      )}
    </CardContent></Card>
  );
}

function AifCard({ inv }) {
  const p = inv.product || {};
  const drawnPct = Math.min(100, (inv.drawn / inv.commitment_amount) * 100);
  return (
    <Card className="border-[var(--glass-border)]" data-testid={`aif-card-${inv.id}`}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 font-heading text-base text-[var(--brand)]"><Building2 className="h-4 w-4" /> {p.name}</CardTitle>
          <div className="flex gap-1.5 text-[10px]">
            <span className="rounded-full bg-[var(--surface-2)] px-2 py-0.5 font-medium">{CAT[p.category]}</span>
            <span className="rounded-full bg-[var(--surface-2)] px-2 py-0.5">{p.sub_type?.replace("_", " ")}</span>
            <span className="rounded-full bg-[var(--surface-2)] px-2 py-0.5">{p.structure === "CLOSED" ? `Close-ended · ${p.tenure_years}y` : "Open-ended"}</span>
            <span className="rounded-full bg-[var(--surface-2)] px-2 py-0.5">{p.tax_status === "PASS_THROUGH" ? "Pass-through tax" : "Fund-level tax"}</span>
          </div>
        </div>
        <p className="text-xs text-[var(--ink-muted)]">{p.manager} · Mgmt {p.mgmt_fee_pct}% · Carry {p.carry_pct}% above {p.hurdle_pct}% hurdle · NAV {p.nav_frequency?.toLowerCase().replace("_", "-")}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-5">
          {[["Commitment", formatCurrency(inv.commitment_amount)],
            ["Drawn", formatCurrency(inv.drawn)],
            ["Current Value", formatCurrency(inv.current_value)],
            ["Distributions", formatCurrency(inv.distributions_total)],
            ["Net XIRR", fmtPct(inv.net_xirr)]].map(([l, v]) => (
            <div key={l} className="rounded-xl bg-[var(--surface-2)] p-2">
              <div className="text-[10px] uppercase text-[var(--ink-muted)]">{l}</div>
              <div className="font-heading text-sm text-[var(--brand)]">{v}</div>
            </div>
          ))}
        </div>
        <div>
          <div className="flex justify-between text-xs text-[var(--ink-muted)]">
            <span>Drawdown progress ({drawnPct.toFixed(0)}%)</span>
            <span>Units {inv.units} @ NAV ₹{inv.latest_nav} ({formatDate(inv.nav_date)})</span>
          </div>
          <div className="mt-1 h-2 rounded-full bg-[var(--surface-2)]"><div className="h-2 rounded-full bg-[var(--cta)]" style={{ width: `${drawnPct}%` }} /></div>
          <div className="mt-1.5 flex gap-3 text-[11px] text-[var(--ink-muted)]">
            <span>TVPI <b className="text-[var(--brand)]">{inv.tvpi ?? "—"}x</b></span>
            <span>DPI <b className="text-[var(--brand)]">{inv.dpi ?? "—"}x</b></span>
            <span>RVPI <b className="text-[var(--brand)]">{inv.rvpi ?? "—"}x</b></span>
            <span>Undrawn {formatCurrency(inv.undrawn)}</span>
          </div>
        </div>
        <div className="divide-y divide-[var(--glass-border)] rounded-xl border border-[var(--glass-border)]">
          {inv.calls.map((c) => (
            <div key={c.id} className="flex items-center justify-between px-3 py-2 text-xs" data-testid={`aif-call-${c.id}`}>
              <div>
                <span className="font-medium">Call #{c.call_no}</span> · {c.purpose}
                <div className="text-[var(--ink-muted)]">Due {formatDate(c.due_date)}{c.paid_date ? ` · Paid ${formatDate(c.paid_date)} @ ₹${c.nav_at_allotment}` : ""}</div>
              </div>
              <div className="text-right">
                <div className="font-medium">{formatCurrency(c.amount)}</div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${CALL_TONE[c.status]}`}>{c.status}</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function PmsCard({ acc }) {
  const s = acc.strategy || {};
  return (
    <Card className="border-[var(--glass-border)]" data-testid={`pms-card-${acc.id}`}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 font-heading text-base text-[var(--brand)]"><Briefcase className="h-4 w-4" /> {s.name}</CardTitle>
          <div className="flex gap-1.5 text-[10px]">
            <span className="rounded-full bg-[var(--surface-2)] px-2 py-0.5 font-medium">{s.type?.replace("_", "-")}</span>
            <span className="rounded-full bg-[var(--surface-2)] px-2 py-0.5">{s.style}</span>
            <span className="rounded-full bg-[var(--surface-2)] px-2 py-0.5">vs {s.benchmark}</span>
          </div>
        </div>
        <p className="text-xs text-[var(--ink-muted)]">
          A/c {acc.account_no} · since {formatDate(acc.inception_date)} · Fees: {s.fee_model === "FIXED" ? `${s.fixed_fee_pct}% fixed` : s.fee_model === "PERFORMANCE" ? `${s.perf_fee_pct}% of profits above ${s.hurdle_pct}%` : `${s.fixed_fee_pct}% + ${s.perf_fee_pct}% above ${s.hurdle_pct}% hurdle`}{s.high_water_mark ? " (high-water mark)" : ""}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-5">
          {[["Capital In", formatCurrency(acc.capital_in_total)],
            ["Current Value", formatCurrency(acc.current_value)],
            ["Gain", `${formatCurrency(acc.gain)} (${fmtPct(acc.gain_pct)})`],
            ["XIRR", fmtPct(acc.xirr)],
            ["High-Water Mark", formatCurrency(acc.high_water_mark)]].map(([l, v]) => (
            <div key={l} className="rounded-xl bg-[var(--surface-2)] p-2">
              <div className="text-[10px] uppercase text-[var(--ink-muted)]">{l}</div>
              <div className="font-heading text-sm text-[var(--brand)]">{v}</div>
            </div>
          ))}
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="h-44">
            <div className="mb-1 text-xs font-medium text-[var(--ink-muted)]">Portfolio value</div>
            <ResponsiveContainer width="100%" height={160} minWidth={0}>
              <LineChart data={acc.valuations}>
                <XAxis dataKey="date" tick={{ fontSize: 8 }} />
                <YAxis tick={{ fontSize: 8 }} width={55} tickFormatter={(v) => `${(v / 100000).toFixed(0)}L`} domain={["auto", "auto"]} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Line type="monotone" dataKey="portfolio_value" stroke="#0a9970" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-xl border border-[var(--glass-border)] p-3 text-xs">
            <div className="mb-1 font-medium text-[var(--brand)]">Estimated annual fees</div>
            <div className="space-y-1 text-[var(--ink-muted)]">
              <div className="flex justify-between"><span>Fixed management fee</span><span>{formatCurrency(acc.fee_estimate.fixed_annual)}</span></div>
              <div className="flex justify-between"><span>Performance fee {s.high_water_mark ? "(post-HWM & hurdle)" : ""}</span><span>{formatCurrency(acc.fee_estimate.performance)}</span></div>
              <div className="flex justify-between"><span>GST @18%</span><span>{formatCurrency(acc.fee_estimate.gst)}</span></div>
              <div className="flex justify-between border-t border-[var(--glass-border)] pt-1 font-medium text-[var(--brand)]"><span>Total</span><span>{formatCurrency(acc.fee_estimate.total_annual)}</span></div>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto rounded-xl border border-[var(--glass-border)]">
          <table className="w-full text-xs" data-testid={`pms-holdings-${acc.id}`}>
            <thead><tr className="border-b border-[var(--glass-border)] text-left text-[var(--ink-muted)]">
              <th className="p-2.5 font-medium">Stock</th><th className="p-2.5 font-medium">Sector</th><th className="p-2.5 text-right font-medium">Qty</th><th className="p-2.5 text-right font-medium">Avg Cost</th><th className="p-2.5 text-right font-medium">LTP</th><th className="p-2.5 text-right font-medium">Value</th><th className="p-2.5 text-right font-medium">Gain</th>
            </tr></thead>
            <tbody className="divide-y divide-[var(--glass-border)]">
              {acc.holdings.map((h) => (
                <tr key={h.symbol}>
                  <td className="p-2.5"><span className="font-medium">{h.symbol}</span> <span className="text-[var(--ink-muted)]">{h.name}</span></td>
                  <td className="p-2.5">{h.sector}</td>
                  <td className="p-2.5 text-right">{h.quantity}</td>
                  <td className="p-2.5 text-right">₹{h.avg_cost}</td>
                  <td className="p-2.5 text-right">₹{h.last_price}</td>
                  <td className="p-2.5 text-right font-medium">{formatCurrency(h.market_value)}</td>
                  <td className={`p-2.5 text-right ${h.gain_pct >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmtPct(h.gain_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Alternates() {
  const [data, setData] = useState(null);
  useEffect(() => { clientApi.get("/alternates/summary").then((r) => setData(r.data)); }, []);

  return (
    <ClientLayout>
      <div className="mb-6">
        <h1 className="flex items-center gap-2 font-heading text-3xl text-[var(--brand)]"><Layers className="h-7 w-7" /> Alternates</h1>
        <p className="mt-1 text-sm text-[var(--ink-muted)]">Your AIF commitments and PMS portfolios — beyond mutual funds.</p>
      </div>
      <NetworthStrip />
      {!data ? <div className="py-10 text-center text-sm text-[var(--ink-muted)]">Loading alternates...</div> : (
        <Tabs defaultValue="aif">
          <TabsList>
            <TabsTrigger value="aif" data-testid="alt-tab-aif">AIF ({data.aif.length})</TabsTrigger>
            <TabsTrigger value="pms" data-testid="alt-tab-pms">PMS ({data.pms.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="aif" className="space-y-5">
            {data.aif.length === 0 ? <Card className="border-[var(--glass-border)]"><CardContent className="p-8 text-center text-sm text-[var(--ink-muted)]" data-testid="no-aif-message">No AIF investments yet. Speak to your SMIFS relationship manager about Category I/II/III alternatives (min ₹1 Cr commitment).</CardContent></Card> :
              data.aif.map((inv) => <AifCard key={inv.id} inv={inv} />)}
          </TabsContent>
          <TabsContent value="pms" className="space-y-5">
            {data.pms.length === 0 ? <Card className="border-[var(--glass-border)]"><CardContent className="p-8 text-center text-sm text-[var(--ink-muted)]" data-testid="no-pms-message">No PMS accounts yet. SMIFS PMS strategies start at ₹50 lakh (SEBI minimum).</CardContent></Card> :
              data.pms.map((acc) => <PmsCard key={acc.id} acc={acc} />)}
          </TabsContent>
        </Tabs>
      )}
    </ClientLayout>
  );
}
